<!--
  AI Document Processor — main composite

  Dashboard for the AI document pipeline:
  - page header with upload actions and auto-refresh control
  - full-page drag-and-drop upload (overlay while dragging)
  - clickable status tiles = server-side status filter
  - server-driven queue table, row click opens the document workbench
-->
<template>
  <div
    class="ai-document-processor q-pa-md column no-wrap q-gutter-md"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop">
    <!-- Drag overlay -->
    <div
      v-if="isDraggingOver"
      class="drop-overlay column items-center justify-center">
      <q-icon
        :name="symRoundedCloudUpload"
        size="64px" />
      <div class="text-h6 q-mt-sm">{{ t('header.dropOverlay') }}</div>
    </div>

    <!-- Header -->
    <div class="row items-center q-col-gutter-sm">
      <div :class="$q.screen.lt.sm ? 'col-12' : 'col'">
        <div class="row items-center no-wrap">
          <div class="col">
            <div class="text-h5 text-weight-medium">{{ t('title') }}</div>
            <div class="text-caption text-grey-6">{{ t('description') }}</div>
          </div>
          <!-- Refresh controls stay next to the title on phones -->
          <div
            v-if="$q.screen.lt.sm"
            class="col-auto row items-center no-wrap">
            <q-toggle
              :model-value="queueManager.autoRefresh.value"
              dense
              @update:model-value="queueManager.toggleAutoRefresh()">
              <q-tooltip>{{ t('header.autoRefresh') }}</q-tooltip>
            </q-toggle>
            <q-btn
              flat
              round
              dense
              :icon="symRoundedRefresh"
              :loading="queueManager.isLoading.value"
              @click="queueManager.refresh()">
              <q-tooltip>{{ t('header.refreshNow') }}</q-tooltip>
            </q-btn>
          </div>
        </div>
      </div>
      <div
        :class="$q.screen.lt.sm ? 'col-12 row q-col-gutter-sm' : 'col-auto row items-center q-gutter-sm'">
        <template v-if="!$q.screen.lt.sm">
          <q-toggle
            :model-value="queueManager.autoRefresh.value"
            dense
            :label="t('header.autoRefresh')"
            @update:model-value="queueManager.toggleAutoRefresh()" />
          <q-btn
            flat
            round
            dense
            :icon="symRoundedRefresh"
            :loading="queueManager.isLoading.value"
            @click="queueManager.refresh()">
            <q-tooltip>{{ t('header.refreshNow') }}</q-tooltip>
          </q-btn>
        </template>
        <div :class="$q.screen.lt.sm ? 'col-6' : 'col-auto'">
          <q-btn
            outline
            color="secondary"
            :icon="symRoundedCamera"
            :label="$q.screen.gt.sm || $q.screen.lt.sm ? t('header.takePhoto') : ''"
            :class="$q.screen.lt.sm ? 'full-width' : ''"
            @click="triggerCameraInput" />
        </div>
        <div :class="$q.screen.lt.sm ? 'col-6' : 'col-auto'">
          <q-btn
            color="primary"
            :icon="symRoundedUploadFile"
            :label="$q.screen.lt.sm ? t('header.uploadShort') : t('header.uploadButton')"
            :class="$q.screen.lt.sm ? 'full-width' : ''"
            @click="triggerFileInput" />
        </div>
      </div>
    </div>

    <input
      ref="fileInputRef"
      type="file"
      multiple
      :accept="fileUpload.supportedMimeTypes.value.concat(['image/*']).join(',')"
      style="display: none"
      @change="onFileInputChange" />

    <!-- Upload tray: visible only while files are queued/uploading -->
    <InfoBox
      v-if="fileUpload.hasFiles.value"
      :label="t('upload.uploadQueue')"
      theme="info">
      <div class="column q-gutter-xs q-py-sm">
        <div
          v-for="file in fileUpload.queuedFiles.value"
          :key="file.id"
          class="row items-center q-gutter-sm">
          <q-icon
            :name="fileIconFor(file.file.type)"
            size="20px"
            color="grey-6" />
          <span
            class="ellipsis"
            style="max-width: 340px">
            {{ file.file.name }}
          </span>
          <q-linear-progress
            v-if="file.status === 'uploading'"
            :value="file.progress / 100"
            color="info"
            size="4px"
            rounded
            style="width: 80px" />
          <StatusPill
            :color="uploadStatusColor(file.status)"
            :label="t(`upload.fileStatus.${file.status}`)" />
          <span
            v-if="file.error"
            class="text-caption text-negative">
            {{ file.error }}
          </span>
          <q-space />
          <q-btn
            v-if="file.status !== 'uploading'"
            flat
            round
            dense
            size="xs"
            :icon="symRoundedClose"
            @click="fileUpload.removeFile(file.id)">
            <q-tooltip>{{ t('upload.removeFile') }}</q-tooltip>
          </q-btn>
        </div>
      </div>
    </InfoBox>

    <!-- Status tiles (filters) -->
    <StatusTiles />

    <!-- Queue table -->
    <QueueTable />
  </div>
</template>

<script setup lang="ts">
import { symRoundedCamera, symRoundedClose, symRoundedCloudUpload, symRoundedRefresh, symRoundedUploadFile } from '@quasar/extras/material-symbols-rounded'
import { useQuasar } from 'quasar'
import { useCompositeI18n } from 'src/CrudManager/Core/composables/useCompositeI18n'
import InfoBox from 'src/CrudManager/Core/composites/Ui/InfoBox/InfoBox.vue'
import StatusPill from 'src/CrudManager/Core/composites/Ui/StatusPill/StatusPill.vue'
import { showErrorNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import { provide, ref, watch } from 'vue'
import { queueManagerKey } from './composables/queueManagerKey'
import { useFileUpload } from './composables/useFileUpload'
import { useQueueManager } from './composables/useQueueManager'
import { fileIconFor } from './utils/statusMaps'
import QueueTable from './components/QueueTable.vue'
import StatusTiles from './components/StatusTiles.vue'

const { t } = useCompositeI18n('core.AiDocumentProcessor')
const $q = useQuasar()

// Single queue manager instance — provided to StatusTiles / QueueTable
const queueManager = useQueueManager()
provide(queueManagerKey, queueManager)

// Surface queue API errors as toasts
watch(queueManager.apiError, (error) => {
  if (error) {
    showErrorNotification(error.message)
  }
})

// ── Upload (file picker, camera, page-wide drag & drop) ──────────────────

const fileUpload = useFileUpload()
const fileInputRef = ref<HTMLInputElement>()
const isDraggingOver = ref(false)
let dragDepth = 0

const triggerFileInput = (): void => {
  if (fileUpload.isUploading.value || !fileInputRef.value) return
  fileInputRef.value.removeAttribute('capture')
  fileInputRef.value.setAttribute('multiple', '')
  fileInputRef.value.click()
}

const triggerCameraInput = (): void => {
  if (fileUpload.isUploading.value || !fileInputRef.value) return
  fileInputRef.value.setAttribute('capture', 'environment')
  fileInputRef.value.removeAttribute('multiple')
  fileInputRef.value.click()
  setTimeout(() => fileInputRef.value?.setAttribute('multiple', ''), 100)
}

const startUpload = async (): Promise<void> => {
  try {
    await fileUpload.uploadFiles()
    await queueManager.refresh()
  } catch (error) {
    showErrorNotification(error instanceof Error ? error.message : t('messages.uploadFailed'))
  }
}

const onFileInputChange = (event: Event): void => {
  fileUpload.handleFileInput(event)
  void startUpload()
}

const onDragEnter = (event: DragEvent): void => {
  if (!event.dataTransfer?.types.includes('Files')) return
  dragDepth++
  isDraggingOver.value = true
}

const onDragOver = (event: DragEvent): void => {
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = 'copy'
  }
}

const onDragLeave = (): void => {
  dragDepth = Math.max(0, dragDepth - 1)
  if (dragDepth === 0) {
    isDraggingOver.value = false
  }
}

const onDrop = (event: DragEvent): void => {
  dragDepth = 0
  isDraggingOver.value = false
  const files = Array.from(event.dataTransfer?.files ?? [])
  if (files.length > 0) {
    fileUpload.addFiles(files)
    void startUpload()
  }
}

const uploadStatusColor = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'positive'
    case 'error':
      return 'negative'
    case 'uploading':
      return 'info'
    default:
      return 'muted'
  }
}
</script>

<style lang="scss" scoped>
.ai-document-processor {
  position: relative;
  min-height: 60vh;
}

.drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  border: 2px dashed var(--q-primary);
  border-radius: 12px;
  background: color-mix(in srgb, var(--q-primary) 10%, transparent);
  color: var(--q-primary);
  pointer-events: none;
  backdrop-filter: blur(1px);
}
</style>
